import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Camera, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API, dateTimeFr, eur, apiError } from "@/lib/api";
import Modal from "@/components/Modal";
import PhotoCapture from "@/components/PhotoCapture";

export default function ArticleDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [mvtOpen, setMvtOpen] = useState(false);
  const [mvt, setMvt] = useState({ type: "entree", quantite: "1", chantier_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/articles/${id}`).then((r) => setData(r.data)).catch(() => navigate("/stocks"));
  useEffect(() => {
    load();
    api.get("/chantiers").then((r) => setChantiers(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;
  const { article, mouvements, photos } = data;
  const bas = article.quantite <= article.stock_minimum;

  async function addMouvement() {
    setBusy(true);
    try {
      await api.post(`/articles/${id}/mouvement`, {
        type: mvt.type, quantite: Number(mvt.quantite) || 0,
        chantier_id: mvt.chantier_id || null,
        note: mvt.type === "entree" ? t("stock.note_reception") : t("stock.note_sortie"),
      });
      toast.success(t("article.mouvement_ok"));
      setMvtOpen(false);
      setMvt({ type: "entree", quantite: "1", chantier_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-article-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/stocks")}
                className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1]"
                data-testid="btn-retour-stocks">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif font-bold text-2xl sm:text-3xl truncate">{article.nom}</h1>
          <p className="text-stone-500 text-sm">
            {article.reference && <>{t("article.ref")} {article.reference} · </>}
            {t("article.alerte")} {article.stock_minimum}
            {article.prix_achat != null && <> · {t("article.achat")} {eur(article.prix_achat)}</>}
          </p>
        </div>
        <span className={`border text-sm font-bold px-3 py-1.5 rounded-full shrink-0 ${
          bas ? "bg-[#FBE4E1] text-[#A32E20] border-[#F5C2BC]" : "bg-[#E4F2E7] text-[#276440] border-[#B8E2C2]"}`}
              data-testid="badge-stock-detail">
          {bas && <AlertTriangle size={13} className="inline mr-1" />}
          {article.quantite}{article.unite ? ` ${article.unite}` : ""} {t("article.en_stock")}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PhotoCapture articleId={id} type="bon_livraison" className="btn-accent"
                      testid="btn-photo-bon-livraison" onUploaded={load}>
          <Camera size={18} /> {t("article.photo_bl")}
        </PhotoCapture>
        <button onClick={() => setMvtOpen(true)} className="btn-secondary" data-testid="btn-mouvement-detail">
          {t("article.mouvement")}
        </button>
      </div>

      {photos.length > 0 && (
        <div className="panel">
          <h2 className="font-serif font-semibold text-lg mb-3">{t("article.galerie")}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="galerie-photos-article">
            {photos.map((p) => (
              <a key={p.id} href={`${API}/photos/${p.id}/file`} target="_blank" rel="noreferrer">
                <img src={`${API}/photos/${p.id}/file`} alt={p.description || t("photo.alt_bl")}
                     className="w-full h-24 object-cover rounded-lg border border-[#E6DFD1]" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="panel !p-0 overflow-hidden">
        <h2 className="font-serif font-semibold text-lg p-5 pb-3">{t("article.historique")}</h2>
        {mouvements.length === 0 ? (
          <p className="text-stone-500 text-sm pb-8 px-5">{t("article.vide")}</p>
        ) : (
          <div className="divide-y divide-[#E6DFD1]" data-testid="liste-mouvements-article">
            {mouvements.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-4 text-sm">
                <History size={16} className="text-stone-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{m.type === "entree" ? t("article.entree") : t("article.sortie")} · {m.note || "—"}</div>
                  <div className="text-xs text-stone-500">
                    {dateTimeFr(m.date)}{m.chantier_titre && <> · {m.chantier_titre}</>}
                  </div>
                </div>
                <span className={`font-serif font-bold shrink-0 ${m.type === "entree" ? "text-[#276440]" : "text-[#A32E20]"}`}>
                  {m.type === "entree" ? "+" : "−"}{m.quantite}{article.unite ? ` ${article.unite}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={mvtOpen} onClose={() => setMvtOpen(false)} title={t("article.modal_titre", { nom: article.nom })} testid="modal-mouvement-detail">
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[["entree", t("article.entree_long")], ["sortie", t("article.sortie_long")]].map(([v, l]) => (
            <button key={v} onClick={() => setMvt({ ...mvt, type: v })} data-testid={`mvt-detail-type-${v}`}
                    className={`h-12 rounded-lg font-semibold text-sm border ${
                      mvt.type === v ? "bg-[#12100D] text-[#D9A461] border-[#D9A461]/40" : "bg-white border-[#E6DFD1] text-stone-600"}`}>
              {l}
            </button>
          ))}
        </div>
        <label className="block text-sm font-medium mb-1.5">{t("article.quantite")}</label>
        <input className="input-field mb-4" type="number" min="0" value={mvt.quantite}
               onChange={(e) => setMvt({ ...mvt, quantite: e.target.value })} data-testid="input-mvt-detail-quantite" />
        <label className="block text-sm font-medium mb-1.5">{t("article.chantier")}</label>
        <select className="input-field mb-5" value={mvt.chantier_id}
                onChange={(e) => setMvt({ ...mvt, chantier_id: e.target.value })} data-testid="select-mvt-detail-chantier">
          <option value="">{t("commun.aucun")}</option>
          {chantiers.map((c) => (
            <option key={c.id} value={c.id}>{c.titre}</option>
          ))}
        </select>
        <button onClick={addMouvement} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-mouvement-detail">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("article.enregistrer")}
        </button>
      </Modal>
    </div>
  );
}
